import { useTranslation } from '../../contexts/LanguageContext'

interface WelcomeScreenProps {
  onCreateFromScratch: () => void
  onImportPdf: () => void
}

export function WelcomeScreen({ onCreateFromScratch, onImportPdf }: WelcomeScreenProps) {
  const { t } = useTranslation()

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col items-center justify-start md:justify-center p-6 py-16 overflow-y-auto animate-fade-in">
      {/* Background decoration — matches landing hero */}
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-violet-500/5 rounded-full blur-3xl" />
      </div>

      {/* Logo */}
      <div className="mb-8 animate-slide-up">
        <div className="flex items-center gap-2.5">
          <img src="/images/logo.png" alt="CVtoday" className="w-9 h-9" />
          <span className="font-display font-bold text-2xl text-gray-900">CVtoday</span>
        </div>
      </div>

      {/* Title */}
      <div className="text-center mb-12 max-w-2xl animate-slide-up-delayed">
        <h2 className="font-display text-2xl md:text-3xl font-bold text-gray-900 mb-3">
          {t.welcome.title}
        </h2>
        <p className="text-gray-500 text-lg leading-relaxed">
          {t.welcome.subtitle}
        </p>
      </div>

      {/* Option Cards */}
      <div className="w-full max-w-4xl grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        {/* Create from Scratch */}
        <button
          onClick={onCreateFromScratch}
          className="group relative bg-white rounded-2xl p-6 text-left border border-gray-100 transition-all duration-300 hover:border-primary/20 hover:shadow-lg hover:shadow-primary/5 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-2 animate-slide-up"
        >
          <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
            <svg className="w-5.5 h-5.5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m3.75 9v6m3-3H9m1.5-12H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>
          </div>

          <h3 className="font-display text-lg font-semibold text-gray-900 mb-2">
            {t.welcome.createFromScratch}
          </h3>
          <p className="text-sm text-gray-500 leading-relaxed">
            {t.welcome.scratchDesc}
          </p>
        </button>

        {/* Import PDF */}
        <button
          onClick={onImportPdf}
          className="group relative bg-white rounded-2xl p-6 text-left border border-gray-100 transition-all duration-300 hover:border-primary/20 hover:shadow-lg hover:shadow-primary/5 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-2 animate-slide-up-delayed"
        >
          <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
            <svg className="w-5.5 h-5.5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
          </div>

          <h3 className="font-display text-lg font-semibold text-gray-900 mb-2">
            {t.welcome.importPdf}
          </h3>
          <p className="text-sm text-gray-500 leading-relaxed">
            {t.welcome.importDesc}
          </p>
        </button>

        {/* Voice - Coming Soon */}
        <div className="relative bg-white rounded-2xl p-6 text-left border border-gray-100 opacity-50 cursor-not-allowed animate-slide-up-delayed-2">
          <div className="absolute top-3 right-3">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
              {t.welcome.voiceComingSoon}
            </span>
          </div>

          <div className="w-11 h-11 rounded-xl bg-gray-100 flex items-center justify-center mb-4">
            <svg className="w-5.5 h-5.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
            </svg>
          </div>

          <h3 className="font-display text-lg font-semibold text-gray-900 mb-2">
            {t.welcome.voiceCreate}
          </h3>
          <p className="text-sm text-gray-500 leading-relaxed">
            {t.welcome.voiceDesc}
          </p>
        </div>
      </div>
    </div>
  )
}
